import React from 'react';
import { useTranslation } from 'react-i18next';
import { Results } from '../../types/results';

interface Props {
  results: Results;
}

const CareersTab: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation();
  
  // Group careers by match percentage range
  const excellentMatch = results.career_recommendations.filter(c => c.match_percentage >= 80);
  const goodMatch = results.career_recommendations.filter(c => c.match_percentage >= 60 && c.match_percentage < 80);
  const moderateMatch = results.career_recommendations.filter(c => c.match_percentage < 60);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold text-gray-800">{t('results.careerRecommendations')}</h3>
        <p className="text-sm text-gray-500">{t('results.matchingCareers')}</p>
      </div>

      {/* Excellent Matches */}
      {excellentMatch.length > 0 && (
        <CareerSection
          title={t('results.excellentMatch') || 'Excellent Match'}
          careers={excellentMatch}
          bgColor="bg-green-50"
          borderColor="border-green-200"
          badgeColor="bg-green-100 text-green-700"
        />
      )}

      {/* Good Matches */}
      {goodMatch.length > 0 && (
        <CareerSection
          title={t('results.goodMatch') || 'Good Match'}
          careers={goodMatch}
          bgColor="bg-blue-50"
          borderColor="border-blue-200"
          badgeColor="bg-blue-100 text-blue-700"
        />
      )}

      {/* Moderate Matches */}
      {moderateMatch.length > 0 && (
        <CareerSection
          title={t('results.moderateMatch') || 'Moderate Match'}
          careers={moderateMatch}
          bgColor="bg-gray-50"
          borderColor="border-gray-200"
          badgeColor="bg-gray-100 text-gray-700"
        />
      )}

      {/* Info note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
        <p className="text-amber-800">
          {t('results.recommendationsInfo')}
        </p>
      </div>
    </div>
  );
};

interface CareerSectionProps {
  title: string;
  careers: Results['career_recommendations'];
  bgColor: string;
  borderColor: string;
  badgeColor: string;
}

const CareerSection: React.FC<CareerSectionProps> = ({
  title,
  careers,
  bgColor,
  borderColor,
  badgeColor
}) => (
  <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
    <h4 className="font-semibold text-gray-800 mb-3 text-sm">{title}</h4>
    <div className="space-y-2">
      {careers.map((career, i) => (
        <div key={i} className="bg-white rounded-lg p-3 flex justify-between items-center">
          <div>
            <h5 className="font-medium text-gray-800">{career.job_title}</h5>
            <p className="text-xs text-gray-500">{career.sector}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-sm font-semibold ${badgeColor}`}>
            {career.match_percentage}%
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default CareersTab;
